import React, { useEffect, useRef } from "react";
import styled from "styled-components";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

const stats = [
  { value: "3", unit: "min", label: "Koha mesatare e rregullimit te flokeve" },
  { value: "180", unit: "g", label: "Featherlight weight" },
  { value: "200", unit: "°C", label: "Max safe temperature" },
  { value: "USB-C", unit: "", label: "Universal charging" },
];

const WhySiara = () => {
  const sectionRef = useRef(null);
  const headingRef = useRef(null);
  const cardsRef = useRef([]);
  const quoteRef = useRef(null);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.fromTo(
        headingRef.current,
        { opacity: 0, y: 36 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: { trigger: headingRef.current, start: "top 82%" },
        },
      );

      cardsRef.current.forEach((card, i) => {
        gsap.fromTo(
          card,
          { opacity: 0, y: 30 },
          {
            opacity: 1,
            y: 0,
            duration: 0.7,
            delay: i * 0.1,
            ease: "power3.out",
            scrollTrigger: { trigger: card, start: "top 88%" },
          },
        );
      });

      gsap.fromTo(
        quoteRef.current,
        { opacity: 0, y: 24 },
        {
          opacity: 1,
          y: 0,
          duration: 1,
          ease: "power3.out",
          scrollTrigger: { trigger: quoteRef.current, start: "top 82%" },
        },
      );
    }, sectionRef);

    return () => ctx.revert();
  }, []);

  return (
    <Section ref={sectionRef} id="why">
      <Inner>
        <HeadingWrap ref={headingRef}>
          <Eyebrow>Why Siara Styler</Eyebrow>
          <SectionTitle>
            Sepse flokët tua nuk duhet të
            <br />
            <em>varet nga një drejtues të ngjitur në mur</em>
          </SectionTitle>
        </HeadingWrap>

        <StatsGrid>
          {stats.map((s, i) => (
            <StatCard key={i} ref={(el) => (cardsRef.current[i] = el)}>
              <StatValue>
                {s.value}
                <StatUnit>{s.unit}</StatUnit>
              </StatValue>
              <StatLabel>{s.label}</StatLabel>
            </StatCard>
          ))}
        </StatsGrid>

        <Comparison>
          <CompareItem $bad>
            <CompareIcon>✗</CompareIcon>
            <CompareText>
              <strong>Drejtuesit tradicional</strong>
              <span>
                Të rëndë, me kabllo, djeg flokë, duhet prizë, merr kohë për tu
                ngohur.
              </span>
            </CompareText>
          </CompareItem>
          <Divider />
          <CompareItem $good>
            <CompareIcon>✓</CompareIcon>
            <CompareText>
              <strong>Siara Styler</strong>
              <span>
                Madhesi xhepi, pa kabllo, Nxehtesi ikonike, USB-C, gati në
                60sekonda, zero dem.
              </span>
            </CompareText>
          </CompareItem>
        </Comparison>

        <Quote ref={quoteRef}>
          <QuoteText>
            "The only hair tool that travels with me everywhere — and actually
            works."
          </QuoteText>
          <QuoteAuthor>— Every woman who's tried Siara</QuoteAuthor>
        </Quote>
      </Inner>
    </Section>
  );
};

const Section = styled.section`
  background: var(--dark);
  position: relative;
  z-index: 3;
  padding: 5rem 3rem;

  @media (max-width: 768px) {
    padding: 4rem 1.4rem;
  }
`;

const Inner = styled.div`
  max-width: 900px;
  margin: 0 auto;
`;

const HeadingWrap = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Eyebrow = styled.p`
  font-family: var(--sans);
  font-size: 0.68rem;
  font-weight: 400;
  letter-spacing: 0.28em;
  text-transform: uppercase;
  color: var(--rose);
  margin-bottom: 0.9rem;
`;

const SectionTitle = styled.h2`
  font-family: var(--serif);
  font-size: clamp(2.2rem, 4vw, 3.8rem);
  font-weight: 300;
  line-height: 1.2;
  color: var(--cream);

  em {
    font-style: italic;
    color: var(--rose);
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
  margin-bottom: 3rem;

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

const StatCard = styled.div`
  background: rgba(255, 255, 255, 0.04);
  border: 1px solid rgba(232, 197, 184, 0.15);
  border-radius: 14px;
  padding: 1.8rem 1.2rem;
  text-align: center;
  transition:
    border-color 0.3s ease,
    background 0.3s ease;

  &:hover {
    border-color: rgba(232, 197, 184, 0.35);
    background: rgba(255, 255, 255, 0.07);
  }
`;

const StatValue = styled.div`
  font-family: var(--serif);
  font-size: clamp(2rem, 3.5vw, 3.2rem);
  font-weight: 300;
  color: var(--cream);
  line-height: 1;
  margin-bottom: 0.35rem;
`;

const StatUnit = styled.span`
  font-size: 1rem;
  color: var(--rose);
  margin-left: 2px;
`;

const StatLabel = styled.p`
  font-family: var(--sans);
  font-size: 0.7rem;
  font-weight: 300;
  letter-spacing: 0.08em;
  color: rgba(253, 248, 245, 0.4);
  text-transform: uppercase;
`;

const Comparison = styled.div`
  display: flex;
  margin-bottom: 3rem;
  border: 1px solid rgba(232, 197, 184, 0.12);
  border-radius: 16px;
  overflow: hidden;

  @media (max-width: 700px) {
    flex-direction: column;
  }
`;

const CompareItem = styled.div`
  flex: 1;
  display: flex;
  gap: 1rem;
  align-items: flex-start;
  padding: 2rem;
  background: ${({ $bad }) =>
    $bad ? "rgba(255,255,255,0.02)" : "rgba(232, 197, 184, 0.07)"};
`;

const CompareIcon = styled.span`
  font-size: 1.1rem;
  color: var(--rose);
  flex-shrink: 0;
  margin-top: 2px;
`;

const CompareText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.4rem;

  strong {
    font-family: var(--serif);
    font-size: 1.05rem;
    font-weight: 400;
    color: var(--cream);
  }

  span {
    font-family: var(--sans);
    font-size: 0.82rem;
    font-weight: 300;
    line-height: 1.6;
    color: rgba(253, 248, 245, 0.5);
  }
`;

const Divider = styled.div`
  width: 1px;
  background: rgba(232, 197, 184, 0.12);
`;

const Quote = styled.div`
  text-align: center;
`;

const QuoteText = styled.p`
  font-family: var(--serif);
  font-size: clamp(1.4rem, 2.5vw, 2.1rem);
  font-weight: 300;
  font-style: italic;
  color: var(--cream);
  line-height: 1.55;
  margin-bottom: 0.8rem;
`;

const QuoteAuthor = styled.p`
  font-family: var(--sans);
  font-size: 0.75rem;
  font-weight: 300;
  letter-spacing: 0.15em;
  color: var(--dusty);
  text-transform: uppercase;
`;

export default WhySiara;
